"use client";
import { Analytics } from "@vercel/analytics/react";
import Link from "next/link";
import { createClient } from '@/utils/supabase/client'; // Make sure to use client version
import { useEffect, useState } from 'react';
import { signOutAction } from "@/app/actions";
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense } from "react";
import ProfilePic from "./ProfilePic";
import styles from './Header.module.css';
import LogoAndNameImage from "../Logo/LogoAndName";
import LogoOnlyImage from "../Logo/Logo";
import {SearchComponent}  from "../SearchComponent/SearchComponent";
import { User } from '@supabase/supabase-js';
import { getProfilePic } from '@/app/actions/profileActions';

function SignUpLink() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const returnPath =  pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '')
  
  return (
    <Link 
    className={styles.signInHeader}
    href={`/sign-up?returnUrl=${encodeURIComponent(returnPath)}`}
    >
      Sign Up
    </Link>
  )
}

const supabase = createClient();

export default function Header() {
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const pathname = usePathname();
  const headerClass = pathname === '/bookmarks' 
  ? "heading-and-logo logo-search-profile with-background" 
  : "heading-and-logo logo-search-profile";

   useEffect(() => {
      console.time('authCheck');
      let isSubscribed = true;
      
      // Try to get from localStorage first for faster initial load
      try {
        const storedUser = localStorage.getItem('zilu.auth.user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          if (isSubscribed) {
            setUser(parsedUser);
            setAuthState('authenticated');
            
            // Also try to get profile pic from localStorage
            const storedPic = localStorage.getItem('zilu.auth.profilePic');
            if (storedPic) {
              setProfilePicUrl(storedPic);
            }
          }
        }
      } catch (e) {
        console.warn('Error reading stored auth state:', e);
      }
      
      // Set up auth state listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (!isSubscribed) return;
        
        const newUser = session?.user || null;
        setUser(newUser);
        setAuthState(newUser ? 'authenticated' : 'unauthenticated');
        
        // Store in localStorage
        if (newUser) {
          localStorage.setItem('zilu.auth.user', JSON.stringify(newUser));
        } else {
          localStorage.removeItem('zilu.auth.user');
          localStorage.removeItem('zilu.auth.profilePic');
          setProfilePicUrl(null);
        }
      });
      
      // Get current auth state if not found in localStorage
      if (authState === 'loading') {
        supabase.auth.getUser().then(({ data }) => {
          if (!isSubscribed) return;
          
          if (data.user) {
            setUser(data.user);
            setAuthState('authenticated');
            localStorage.setItem('zilu.auth.user', JSON.stringify(data.user));
          } else {
            setAuthState('unauthenticated');
          }
        });
      }
      
      console.timeEnd('authCheck');
      
      return () => {
        isSubscribed = false;
        subscription.unsubscribe();
      };
    }, []);
  useEffect(() => {
    // Only run if authenticated
    if (authState !== 'authenticated' || !user) return;
    
    // If we already have a profile pic, no need to fetch
    if (profilePicUrl) return;
    
    // Fetch profile pic
    getProfilePic().then(({ profilePicUrl: newUrl }) => {
      if (newUrl) {
        setProfilePicUrl(newUrl);
        localStorage.setItem('zilu.auth.profilePic', newUrl);
      }
    });
  }, [authState, user, profilePicUrl]);
  
  return (
       <div className={headerClass}>
       <Analytics />
        <div className="desktop-logo desktop">
          <Link href="/">
            <LogoAndNameImage className="desktop-logo-svg" priority/>
          </Link>
          </div>
          <div className="mobile-logo mobile">
          <Link href="/">
            <LogoOnlyImage className="mobile-logo-svg" priority/>
          </Link>
          </div>
          <SearchComponent/>
          {user ? (
            <>
              <div className={styles.profileContainer}>
            <ProfilePic profilePicUrl={profilePicUrl} width={24} height={24} />
                </div>
            </>
            ) : (
            <Suspense fallback={
              <Link className="sign-in-header" href="/sign-up">
                Sign Up
              </Link>
            }>
              <SignUpLink />
            </Suspense>
      )}
          {/* <Link className="sign-in-header" href={"/sign-in"}>Sign In</Link> */}
          {/* need to add burger functionality  */}
            <button className="mobile-burger mobile "
               onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" >
                    <path d="M4 6.25C4 5.83579 4.33579 5.5 4.75 5.5H15.25C15.6642 5.5 16 5.83579 16 6.25C16 6.66421 15.6642 7 15.25 7H4.75C4.33579 7 4 6.66421 4 6.25ZM4 10C4 9.58578 4.33579 9.25 4.75 9.25H15.25C15.6642 9.25 16 9.58578 16 10C16 10.4142 15.6642 10.75 15.25 10.75H4.75C4.33579 10.75 4 10.4142 4 10ZM4 13.75C4 13.3358 4.33579 13 4.75 13H15.25C15.6642 13 16 13.3358 16 13.75C16 14.1642 15.6642 14.5 15.25 14.5H4.75C4.33579 14.5 4 14.1642 4 13.75Z" fill="#1E1C1C"/>
                </svg>
            </button>
            <button className="desktop drop-down-chevron"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-down-chevron">
                  <path d="M6.41107 9.74408C6.73651 9.41864 7.26414 9.41864 7.58958 9.74408L12.0003 14.1548L16.4111 9.74408C16.7365 9.41864 17.2641 9.41864 17.5896 9.74408C17.915 10.0695 17.915 10.5972 17.5896 10.9226L12.5896 15.9226C12.2641 16.248 11.7365 16.248 11.4111 15.9226L6.41107 10.9226C6.08563 10.5972 6.08563 10.0695 6.41107 9.74408Z" fill="#1E1C1C"/>
                </svg>
              </button>
            {isDropdownOpen && (
          <div className="dropdown-menu">
          <Link   href="mailto:feedback@zundoku.com?subject=Feedback&body=Hi%20there%2C%0A%0AI'd%20like%20to%20provide%20some%20feedback%3A%0A%0A"
          className="dropdown-item">
            Give Feedback
          </Link>
          <Link href="/privacy-policy" className="dropdown-item">
            Privacy Policy
          </Link>
          <Link href="/terms-of-service" className="dropdown-item">
            Terms of Service
          </Link>
          <Link href="/impressum" className="dropdown-item">
          Impressum
          </Link>
          <Link href="/cookie-policy" className="dropdown-item">
            Cookie Policy
          </Link>
          {user ? (
          <button onClick={() => signOutAction()} className="dropdown-item">
           Sign Out
          </button>
): (<></>)}
        </div>
      )}
        </div> 
  )
}
